import dayjs from 'dayjs';

import {
  type AgentHubFilters,
  type CardResult,
  type MeasureKey,
  PRODUCT_VERTICALS,
  type ProductVertical,
  type TrendPoint,
  bucketOf,
  lastCompleteDay,
  trendOf,
} from './model';
import { type DateRange } from './period';

const DEFAULT_PERIODS = 24;
const TREND_MONTHS = 60;
export type ChartRange = DateRange & { clamped: boolean };

/** Inclusive trailing civil days. Calendar months clamp month ends before adding the inclusive day. */
export function chartRangeOf(
  filters: Pick<AgentHubFilters, 'chartRange' | 'chartRangeCount'>,
  now = new Date(),
): ChartRange | null {
  if (filters.chartRange === 'default') return null;
  const end = dayjs.utc(lastCompleteDay(now));
  const earliest = end.startOf('month').subtract(TREND_MONTHS - 1, 'month');
  // Bound arithmetic before calling dayjs, even for very large (but valid) URL inputs.
  const maximum =
    filters.chartRange === 'day'
      ? end.diff(earliest, 'day') + 1
      : filters.chartRange === 'week'
        ? Math.ceil((end.diff(earliest, 'day') + 1) / 7)
        : TREND_MONTHS;
  const count = Number(filters.chartRangeCount);
  const requested = end.subtract(Math.min(count, maximum + 1), filters.chartRange).add(1, 'day');
  const clamped = count > maximum || requested.isBefore(earliest);
  return { from: (clamped ? earliest : requested).format('YYYY-MM-DD'), to: end.format('YYYY-MM-DD'), clamped };
}

/** Keep whole overlapping periods so additive values and the cadence's distinct counts agree. */
export function filterTrendBuckets<T extends { bucket: string }>(
  points: Array<T>,
  range: ChartRange | null,
  cadence: AgentHubFilters['cadence'],
): Array<T> {
  if (!range) return points;
  const first = bucketOf(range.from, cadence);
  return points.filter((point) => point.bucket >= first && point.bucket <= range.to);
}

/** Default preserves every loaded bucket and opens the slider on the last 24, as on master. */
export function trendZoomOf(pointCount: number, range: ChartRange | null) {
  return {
    startValue: range ? 0 : Math.max(0, pointCount - DEFAULT_PERIODS),
    endValue: pointCount - 1,
  };
}

export const canSplitVertical = (metric: MeasureKey) =>
  ['grossSales', 'margin', 'netMargin', 'commission', 'agentNett', 'override'].includes(metric);

export function trendCardIds(filters: Pick<AgentHubFilters, 'tab' | 'splitVertical' | 'chartMetric'>) {
  const ids = [
    'o_kpi',
    'o_kpi_counts',
    'o_trend',
    ...['day', 'week', 'month', 'quarter', 'year'].map((cadence) => `o_trend_counts_${cadence}`),
    ...['agent_path', 'product', 'category', 'offer_country', 'vendor', 'agency_country'].flatMap((split) => [
      `o_split_${split}`,
      `o_split_${split}_orders`,
    ]),
    'a_kpi_signups',
    'a_kpi_status',
    'a_signup_trend',
    'a_agents',
    'f_items',
  ];
  return filters.tab === 'orders' && filters.splitVertical && canSplitVertical(filters.chartMetric)
    ? [...ids, 'o_trend_vertical']
    : ids;
}

export type VerticalTrendPoint = TrendPoint & { verticals: Record<ProductVertical, TrendPoint['measures']> };

export function verticalTrendOf(
  daily: CardResult,
  filters: Pick<AgentHubFilters, 'cadence' | 'refunds' | 'from' | 'to'>,
): Array<VerticalTrendPoint> {
  const byVertical = PRODUCT_VERTICALS.map(
    (vertical) =>
      new Map(
        trendOf(
          { ...daily, rows: daily.rows.filter((row) => row.product_vertical === vertical) },
          { problem: 'No distinct counts in a product split.' },
          filters,
        ).map((point) => [point.bucket, point.measures]),
      ),
  );
  return trendOf(daily, { problem: 'No distinct counts in a product split.' }, filters).map((point) => ({
    ...point,
    verticals: Object.fromEntries(
      PRODUCT_VERTICALS.map((vertical, index) => [
        vertical,
        byVertical[index].get(point.bucket) ?? Object.fromEntries(Object.keys(point.measures).map((key) => [key, 0])),
      ]),
    ) as Record<ProductVertical, TrendPoint['measures']>,
  }));
}
